import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Text,
    Textarea,
    Button,
    Alert,
    Loader,
    Badge,
    Paper,
    Group,
    Tabs
} from '@mantine/core';
import {
    IconX,
    IconClipboard,
    IconTool,
    IconSearch,
    IconInfoCircle
} from '@tabler/icons-react';
import { ScimFilterBuilder, FilterGroup, FieldOption } from './ScimFilterBuilder';
import { ScimFilterManualInput } from './ScimFilterManualInput';
import { parseScimFilter, validateScimFilter, isApiExtensionFilter } from './ScimFilterParser';
import { generateScimFilter } from './ScimFilterUtils';
import { useSchemas, useSchemaLoading, useSchemaError } from '../../store/schemaHooks';
import { useAdvancedSearchFilter, useAdvancedSearchActions } from '../../store/advancedSearchHooks';
import { ScimAuthService } from '../../services/ScimAuthService';
import { entityService } from '../../services/entityServiceSingleton';
import EntitlementsService from '../../services/EntitlementsService';
import { buildFormAttributesFromTier2AttributeBindings, buildFormAttributesFromTier2Attributes, isEntitySetFacade } from '../../types/EntityEntitlements';
import { combineScimFiltersWithAnd } from '../../utils/simpleSearchUtils';
import { debugLog } from '../../utils/debugLog';

/** Normalize filter group so attribute names in conditions use API form (no $. prefix). */
function normalizeFilterGroupFields(group: FilterGroup): FilterGroup {
    return {
        ...group,
        conditions: group.conditions.map(c => ({
            ...c,
            field: c.field?.startsWith('$.') ? c.field.slice(2) : c.field
        })),
        groups: group.groups.map(g => normalizeFilterGroupFields(g))
    };
}

export interface PhilProps {
    entityType: string;
    onFilterChange?: (filterString: string) => void;
    onApplyFilter?: (filterString: string) => void;
    initialFilter?: string;
    title?: string;
    showManualInput?: boolean;
    showBuilder?: boolean;
    showGeneratedFilter?: boolean;
    compact?: boolean;
    /** When set, preview count and apply will combine advanced filter with this simple search. */
    currentSimpleSearchText?: string | null;
    /** When set, preview count also includes a hidden simple-search-derived SCIM filter. */
    currentSimpleDerivedFilter?: string | null;
    /** Search In attributes for simple search (comma-joined for API when requesting preview). */
    currentSearchAttributes?: string[] | null;
    /** Forces Phil into one tab while reusing the same internal state. */
    forcedMode?: 'manual' | 'wizard';
    /** Hides the internal manual/wizard tabs when the parent supplies them. */
    hideModeTabs?: boolean;
}

export const Phil: React.FC<PhilProps> = ({
    entityType,
    onFilterChange,
    onApplyFilter,
    initialFilter = '',
    title,
    showManualInput = true,
    showBuilder = true,
    showGeneratedFilter = true,
    compact = false,
    currentSimpleSearchText,
    currentSimpleDerivedFilter,
    currentSearchAttributes,
    forcedMode,
    hideModeTabs = false
}) => {
    // Redux hooks for saving/restoring filters
    const savedFilter = useAdvancedSearchFilter(entityType || '');
    const { setFilter: saveFilter } = useAdvancedSearchActions();

    // Use saved filter from Redux if no initialFilter is provided
    const effectiveInitialFilter = initialFilter || savedFilter || '';

    // State management
    const [scimFilterString, setScimFilterString] = useState(effectiveInitialFilter);

    // When parent passes a new initialFilter (e.g. after fetching last adv filter on Advanced tab open), sync manual filter
    useEffect(() => {
        setScimFilterString(effectiveInitialFilter);
        setShowManualMode(!!effectiveInitialFilter);
    }, [effectiveInitialFilter]);
    const [rootGroup, setRootGroup] = useState<FilterGroup>({
        id: 'root',
        conditions: [],
        groups: [],
        operator: 'and'
    });
    const [fieldOptions, setFieldOptions] = useState<FieldOption[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [authResolved, setAuthResolved] = useState(false);
    const [isConverting, setIsConverting] = useState(false);
    const [showManualMode, setShowManualMode] = useState(!!effectiveInitialFilter);
    const [previewCount, setPreviewCount] = useState<number | null>(null);
    const [loadingPreviewCount, setLoadingPreviewCount] = useState(false);

    useEffect(() => {
        if (!forcedMode) return;
        setShowManualMode(forcedMode === 'manual');
    }, [forcedMode]);

    // Get schemas from Redux
    const schemas = useSchemas();
    const schemaLoadingRedux = useSchemaLoading();
    const schemaErrorRedux = useSchemaError();

    // Load field options for the specific entity type (schema first, then tier2 entitlements fallback)
    const loadFieldOptions = useCallback(async () => {
        if (!authResolved || !entityType?.trim()) return;

        debugLog(`🔍 Phil: Loading field options for entity type: ${entityType}`);
        setError(null);

        const schemaId = entityType.trim().toLowerCase();

        // 1) Try Redux schema first
        if (schemas && schemas.length > 0) {
            let entitySchema = schemas.find(schema =>
                schema.name.toLowerCase() === schemaId
            );
            if (!entitySchema) {
                entitySchema = schemas.find(schema =>
                    schema.id === entityType ||
                    schema.id === `urn:dcps:scim:v1:${entityType}` ||
                    schema.id === `urn:ietf:params:scim:schemas:core:2.0:${entityType}`
                );
            }
            if (entitySchema) {
                const options: FieldOption[] = (entitySchema.attributes || [])
                    .map((attr: any) => {
                        let typeDisplay = '';
                        if (attr.type && attr.type !== 'string') {
                            typeDisplay = ` (${attr.type}`;
                            if (attr.multiValued || attr.Array) typeDisplay += '[]';
                            typeDisplay += ')';
                        } else if (attr.multiValued || attr.Array) {
                            typeDisplay = ' (string[])';
                        }
                        return {
                            value: attr.name,
                            label: `${attr.name}${typeDisplay}`
                        };
                    })
                    .sort((a: FieldOption, b: FieldOption) => a.value.localeCompare(b.value));
                setFieldOptions(options);
                return;
            }
        }

        // 2) Fallback: tier2 entitlements (use cache from table view, then AttributeBindings or Attributes)
        try {
            let tier2 = EntitlementsService.getCachedEntityEntitlementForSchemaEntity(schemaId);
            if (!tier2) {
                tier2 = await EntitlementsService.fetchEntityEntitlementForSchemaEntity(schemaId);
            }
            if (tier2) {
                const formAttrs = isEntitySetFacade(tier2)
                    ? buildFormAttributesFromTier2Attributes(tier2.Attributes)
                    : (buildFormAttributesFromTier2AttributeBindings(tier2.AttributeBindings) ?? buildFormAttributesFromTier2Attributes(tier2.Attributes));
                if (formAttrs.length > 0) {
                    const options: FieldOption[] = formAttrs
                        .map((a) => {
                            const rawName = a.value ?? a.name;
                            if (!rawName) return null;
                            // Use API-style attribute name (no $. prefix) for filter list and SCIM filter
                            const value = rawName.startsWith('$.') ? rawName.slice(2) : rawName;
                            const rawLabel = a.label ?? rawName;
                            const labelBase = rawLabel.startsWith('$.') ? rawLabel.slice(2) : rawLabel;
                            const typeDisplay = a.type && a.type !== 'string' ? ` (${a.type})` : '';
                            return {
                                value,
                                label: `${labelBase}${typeDisplay}`
                            };
                        })
                        .filter((o): o is FieldOption => o != null)
                        .sort((a, b) => a.value.localeCompare(b.value));
                    setFieldOptions(options);
                    return;
                }
            }
        } catch (err) {
            console.warn(`⚠️ Phil: Tier2 fallback failed for ${entityType}:`, err);
        }

        setFieldOptions([]);
        setError('No schema data available. Use tier2 entitlements when available; ensure the service is initialized.');
    }, [entityType, authResolved, schemas]);

    // Handle authentication resolution
    useEffect(() => {
        const checkAuth = async () => {
            try {
                await ScimAuthService.getAuthToken();
                setAuthResolved(true);
            } catch (err) {
                debugLog('⏳ Phil: Waiting for authentication...');
            }
        };
        checkAuth();
    }, []);

    // Load field options when auth is resolved
    useEffect(() => {
        if (authResolved) {
            loadFieldOptions();
        }
    }, [authResolved, loadFieldOptions]);

    // Parse initial filter and populate builder when it changes
    useEffect(() => {
        if (initialFilter && initialFilter !== scimFilterString) {
            setScimFilterString(initialFilter);

            // Try to parse the filter and populate the builder
            try {
                const parsedFilter = parseScimFilter(initialFilter);
                if (parsedFilter) {
                    setRootGroup(normalizeFilterGroupFields(parsedFilter));
                    setShowManualMode(false); // Switch to builder mode if we can parse it
                } else {
                    setShowManualMode(true); // Stay in paste mode if we can't parse it
                }
            } catch (error) {
                console.warn('Could not parse initial filter for builder:', error);
                setShowManualMode(true); // Stay in paste mode if parsing fails
            }
        }
    }, [initialFilter]);

    // If we have a saved filter (Redux) but no explicit initialFilter prop, ensure the builder is seeded.
    // Otherwise the UI can show a generated filter while the builder has no conditions, and clicking Search appears to do nothing.
    useEffect(() => {
        if (!effectiveInitialFilter) return;
        if (initialFilter) return; // initialFilter effect above owns this case

        const hasBuilderCriteria = rootGroup.conditions.length > 0 || rootGroup.groups.length > 0;
        if (hasBuilderCriteria) return;

        try {
            const parsed = parseScimFilter(effectiveInitialFilter);
            if (parsed) {
                setRootGroup(normalizeFilterGroupFields(parsed));
            }
        } catch {
            // Ignore parse errors; user can stay in paste mode.
        }
    }, [effectiveInitialFilter, entityType]);

    // Handle SCIM filter string changes
    const handleScimFilterChange = useCallback((filterString: string) => {
        setScimFilterString(filterString);
        setPreviewCount(null); // Clear preview count when filter changes
        if (onFilterChange) {
            onFilterChange(filterString);
        }
    }, [onFilterChange]);

    // Convert SCIM filter string to builder format
    const handleConvertToBuilder = useCallback(async () => {
        if (!scimFilterString.trim()) {
            setError('Please enter a SCIM filter string to convert');
            return;
        }

        try {
            setIsConverting(true);
            setError(null);
            setInfo(null);

            debugLog(`🔍 Phil: Converting SCIM filter to builder format:`, scimFilterString);

            // Validate the filter
            const validation = validateScimFilter(scimFilterString);
            if (!validation.isValid) {
                setError(`Invalid SCIM filter: ${validation.error}`);
                return;
            }

            // Parse the filter
            const parsedFilter = parseScimFilter(scimFilterString);
            if (!parsedFilter) {
                if (isApiExtensionFilter(scimFilterString)) {
                    setError(null);
                    setInfo('This filter uses API extensions (e.g. property $count) and cannot be edited in the builder. Keep it in manual mode; preview and search will use it as-is.');
                } else {
                    setError('Failed to parse SCIM filter');
                }
                return;
            }

            debugLog(`✅ Phil: Successfully converted filter to builder format:`, parsedFilter);
            setInfo(null);
            setRootGroup(normalizeFilterGroupFields(parsedFilter));
            setShowManualMode(false);

        } catch (err) {
            console.error(`❌ Phil: Error converting filter:`, err);
            setError(`Failed to convert filter: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally {
            setIsConverting(false);
        }
    }, [scimFilterString]);

    // Handle builder changes
    const handleBuilderChange = useCallback((newRootGroup: FilterGroup) => {
        setRootGroup(newRootGroup);

        // Generate SCIM filter string from builder
        const generatedFilter = generateScimFilter(newRootGroup);
        setScimFilterString(generatedFilter);
        setPreviewCount(null); // Clear preview count when filter changes

        if (onFilterChange) {
            onFilterChange(generatedFilter);
        }
    }, [onFilterChange]);

    // Generate current filter string
    // In builder mode, fall back to the last known scimFilterString if the builder is empty.
    const generatedFromBuilder = generateScimFilter(rootGroup);
    const currentFilterString = showManualMode ? scimFilterString : (generatedFromBuilder || scimFilterString);

    // Handle apply filter
    const handleApplyFilter = useCallback(() => {
        const filterString = currentFilterString;

        // Save filter to Redux
        if (entityType && filterString) {
            saveFilter(entityType, filterString);
        }

        if (onApplyFilter) {
            onApplyFilter(filterString);
        }
    }, [currentFilterString, onApplyFilter, entityType, saveFilter]);

    // Handle preview count (combines advanced filter with current simple search when provided)
    const handlePreviewCount = useCallback(async () => {
        const filterString = currentFilterString;
        if (!filterString) {
            return;
        }

        setLoadingPreviewCount(true);
        setPreviewCount(null);
        try {
            const searchtext = (currentSimpleSearchText ?? '').trim() || undefined;
            const effectiveFilterString = combineScimFiltersWithAnd(currentSimpleDerivedFilter, filterString);
            const searchAttributesStr = (currentSearchAttributes && currentSearchAttributes.length > 0)
                ? currentSearchAttributes.join(',')
                : undefined;
            const response = await entityService.getEntitiesByType(
                entityType,
                1,
                2, // maxCount = 2 like legacy bulk
                undefined,
                effectiveFilterString,
                undefined, // attributes
                undefined, // abortSignal
                searchtext,
                searchAttributesStr
            );
            setPreviewCount(response.totalResults || 0);
        } catch (err) {
            console.error('Error previewing filter count:', err);
            setError(err instanceof Error ? err.message : 'Failed to preview count');
            setPreviewCount(null);
        } finally {
            setLoadingPreviewCount(false);
        }
    }, [currentFilterString, entityType, currentSimpleDerivedFilter, currentSimpleSearchText, currentSearchAttributes]);

    // Clear all filters
    const handleClear = useCallback(() => {
        setScimFilterString('');
        setPreviewCount(null); // Clear preview count when clearing
        setRootGroup({
            id: 'root',
            conditions: [],
            groups: [],
            operator: 'and'
        });
        setError(null);
        if (onFilterChange) {
            onFilterChange('');
        }
    }, [onFilterChange]);

    const displayTitle = title || `Advanced Search for ${entityType}`;

    if (schemaLoadingRedux) {
        return (
            <Group justify="center" align="center" p="md" gap="sm">
                <Loader size="sm" />
                <Text size="sm">
                    Loading field options for {entityType}...
                </Text>
            </Group>
        );
    }

    return (
        <Paper withBorder p={compact ? 'md' : 'lg'}>
            {/* Header */}
            <Group justify="space-between" align="center" mb="md">
                <Text fw={600} size="lg" component="h2">
                    {displayTitle}
                </Text>
                <Group gap="xs">
                    <Badge variant="outline" color="blue" size="sm">
                        {entityType}
                    </Badge>
                    {fieldOptions.length > 0 && (
                        <Badge variant="outline" color="gray" size="sm">
                            {fieldOptions.length} fields
                        </Badge>
                    )}
                </Group>
            </Group>

            {/* Error Display */}
            {error && (
                <Alert color="red" mb="md" withCloseButton onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {info && (
                <Alert color="blue" mb="md" withCloseButton onClose={() => setInfo(null)} title="Note">
                    {info}
                </Alert>
            )}

            {/* Manual Filter vs Filter Wizard tabs */}
            {!hideModeTabs && (
                <Tabs value={showManualMode ? 'manual' : 'wizard'} onChange={(v) => {
                    if (v === 'manual') {
                        setShowManualMode(true);
                        setError(null);
                        setInfo(null);
                    } else {
                        // If we already have a filter string (from paste mode / redux), seed the builder
                        // so "Filter Wizard" doesn't appear blank while "Generated Filter" shows a value.
                        const hasBuilderCriteria = rootGroup.conditions.length > 0 || rootGroup.groups.length > 0;
                        if (!hasBuilderCriteria && scimFilterString?.trim()) {
                            try {
                                const parsed = parseScimFilter(scimFilterString);
                                if (parsed) {
                                    setRootGroup(normalizeFilterGroupFields(parsed));
                                }
                            } catch {
                                // Ignore; user can still use paste mode.
                            }
                        }
                        setShowManualMode(false);
                        setError(null);
                    }
                }} mb="md">
                    <Tabs.List>
                        <Tabs.Tab value="manual" leftSection={<IconClipboard size={16} />}>
                            Manual Filter
                        </Tabs.Tab>
                        <Tabs.Tab value="wizard" leftSection={<IconTool size={16} />}>
                            Filter Wizard
                        </Tabs.Tab>
                    </Tabs.List>
                </Tabs>
            )}

            {/* Paste Input Mode */}
            {showManualInput && showManualMode && (
                <Box mb="lg">
                    <ScimFilterManualInput
                        value={scimFilterString}
                        onChange={handleScimFilterChange}
                        onApplyEnter={handleApplyFilter}
                        onFilterParsed={(filterGroup) => {
                            setRootGroup(filterGroup);
                            setShowManualMode(false);
                        }}
                        onClear={() => {
                            setScimFilterString('');
                            setRootGroup({
                                id: 'root',
                                conditions: [],
                                groups: [],
                                operator: 'and'
                            });
                        }}
                        placeholder={`Type or paste a SCIM filter for ${entityType} (press Enter to search)`}
                        label="SCIM Filter String"
                        helperText={`Type or paste a SCIM filter string. Press Enter to apply and search.`}
                    />
                </Box>
            )}

            {/* Builder Mode */}
            {showBuilder && !showManualMode && (
                <Box mb="lg">
                    <ScimFilterBuilder
                        rootGroup={rootGroup}
                        onUpdateFilter={handleBuilderChange}
                        fieldOptions={fieldOptions}
                        entityType={entityType}
                    />
                </Box>
            )}

            {/* Generated Filter Display */}
            {showGeneratedFilter && currentFilterString && (
                <Box mb="md">
                    <Text size="sm" fw={500} mb="xs">
                        Generated Filter:
                    </Text>
                    <Textarea
                        value={currentFilterString}
                        minRows={2}
                        readOnly
                        size="sm"
                        styles={{
                            input: {
                                fontFamily: 'monospace',
                                fontSize: '0.875rem'
                            }
                        }}
                    />
                </Box>
            )}

            {/* Action Buttons */}
            <Group justify="flex-end" align="center" gap="xs">
                {previewCount !== null && (
                    <Text size="sm" c="dimmed" mr="xs">
                        Count: {previewCount}
                    </Text>
                )}
                <Button
                    onClick={handleClear}
                    leftSection={<IconX size={16} />}
                    disabled={!currentFilterString}
                >
                    Clear
                </Button>
                <Button
                    onClick={handlePreviewCount}
                    variant="outline"
                    disabled={!currentFilterString || loadingPreviewCount}
                    leftSection={loadingPreviewCount ? <Loader size={16} /> : <IconInfoCircle size={16} />}
                >
                    Preview Count
                </Button>
                <Button
                    onClick={handleApplyFilter}
                    variant="filled"
                    leftSection={<IconSearch size={16} />}
                    disabled={!currentFilterString}
                >
                    Search
                </Button>
            </Group>

            {/* Info */}
            {fieldOptions.length === 0 && (
                <Alert color="blue" mt="md">
                    <Group gap="xs">
                        <IconInfoCircle size={18} />
                        <Text size="sm">
                            No field options available for {entityType}.
                            You can still type or paste a SCIM filter string or build a basic filter.
                        </Text>
                    </Group>
                </Alert>
            )}
        </Paper>
    );
};
